import React from 'react';
import { AspectMapping } from 'services/categoryMappingService';

interface GuidanceRowProps {
  aspect: AspectMapping;
  colSpan: number;
}

interface Band {
  label: string;
  range: string;
  headerBg: string;
  headerText: string;
  scoreCls: string;
  descCls: string;
  entries: [string, string][];
}

/**
 * Renders the 1–10 score guidance for a single aspect as an inline table row.
 * Scores are grouped into three side-by-side columns: Low (1–4), Mid (5–7), High (8–10).
 */
const GuidanceRow: React.FC<GuidanceRowProps> = ({ aspect, colSpan }) => {
  const all = Object.entries(aspect.scaleGuidance).sort(
    ([a], [b]) => Number(a) - Number(b)
  );

  if (all.length === 0) {
    return (
      <tr className="bg-gray-50">
        <td
          colSpan={colSpan}
          className="px-4 py-2 text-xs text-gray-400 italic border-b border-gray-100"
        >
          No scale guidance defined in CMS.
        </td>
      </tr>
    );
  }

  const bands: Band[] = [
    {
      label: 'Low',
      range: '1–4',
      headerBg: 'bg-red-50',
      headerText: 'text-red-600',
      scoreCls: 'text-red-600',
      descCls: 'text-red-700',
      entries: all.filter(([k]) => Number(k) <= 4) as [string, string][],
    },
    {
      label: 'Mid',
      range: '5–7',
      headerBg: 'bg-yellow-50',
      headerText: 'text-yellow-600',
      scoreCls: 'text-yellow-600',
      descCls: 'text-yellow-700',
      entries: all.filter(([k]) => Number(k) >= 5 && Number(k) <= 7) as [
        string,
        string,
      ][],
    },
    {
      label: 'High',
      range: '8–10',
      headerBg: 'bg-green-50',
      headerText: 'text-green-600',
      scoreCls: 'text-green-600',
      descCls: 'text-green-700',
      entries: all.filter(([k]) => Number(k) >= 8) as [string, string][],
    },
  ];

  return (
    <tr className="bg-gray-50 border-b border-gray-200">
      <td colSpan={colSpan} className="px-4 py-1.5">
        <div className="grid grid-cols-3 gap-2 text-xs">
          {bands.map(band => (
            <div
              key={band.label}
              className="border border-gray-200 rounded overflow-hidden"
            >
              {/* Column header */}
              <div
                className={`flex items-center gap-1.5 px-2 py-1 ${band.headerBg} border-b border-gray-200`}
              >
                <span
                  className={`font-bold uppercase tracking-widest ${band.headerText}`}
                >
                  {band.label}
                </span>
                <span className="text-gray-400 font-normal">{band.range}</span>
              </div>

              {/* Score rows */}
              {band.entries.length === 0 ? (
                <p className="px-2 py-1 text-gray-300 italic">—</p>
              ) : (
                <table className="w-full border-collapse">
                  <tbody>
                    {band.entries.map(([key, desc]) => (
                      <tr
                        key={key}
                        className="border-b border-gray-100 last:border-0"
                      >
                        <td
                          className={`py-0.5 pl-2 pr-1.5 align-top font-bold tabular-nums w-5 ${band.scoreCls}`}
                        >
                          {key}
                        </td>
                        <td className={`py-0.5 pr-2 leading-snug align-top`}>
                          {desc || (
                            <span className="italic text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))}
        </div>
      </td>
    </tr>
  );
};

export default GuidanceRow;
